import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Space, 
  Table, 
  message, 
  Spin, 
  Typography, 
  Row, 
  Col, 
  Tag, 
  Divider,
  Modal,
  Tooltip,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  PlayCircleOutlined, 
  InfoCircleOutlined,
  ExperimentOutlined,
  BarChartOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createTask } from '@/utils/taskManager';
import TaskList from '@/components/management/TaskList';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface AntibodyInput {
  id: string;
  name: string;
  heavy_chain: string;
  light_chain: string;
}

interface ViscosityResult {
  name: string;
  prob_mean: number;
  prob_std: number;
  viscosity_class: number;
  viscosity_label: string;
}

interface TaskResult {
  task_id: string;
  status: string;
  results?: ViscosityResult[];
  total_count?: number;
  processing_time?: number;
  message?: string;
  created_at?: string;
  updated_at?: string;
  started_at?: string;
  completed_at?: string;
  progress?: number;
  error?: string;
}

export default function AntibodyViscosityPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [antibodies, setAntibodies] = useState<AntibodyInput[]>([
    { id: '1', name: '', heavy_chain: '', light_chain: '' }
  ]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ViscosityResult[]>([]);
  const [taskId, setTaskId] = useState<string>('');
  const [showResults, setShowResults] = useState(false);
  const [taskStatus, setTaskStatus] = useState<TaskResult | null>(null);
  const [taskHistory, setTaskHistory] = useState<TaskResult[]>([]);
  const [submittedAntibodies, setSubmittedAntibodies] = useState<AntibodyInput[]>([]);
 
  const extractViscosityResults = (payload: any): ViscosityResult[] => {
    if (!payload) {
      return [];
    }

    if (payload && typeof payload === 'object' && 'name' in payload && 'viscosity_class' in payload) {
      return [payload as ViscosityResult];
    }

    if (Array.isArray(payload.results)) {
      const directMatches = payload.results.filter((item: any) => item && typeof item === 'object' && 'name' in item && 'viscosity_class' in item);
      if (directMatches.length === payload.results.length && directMatches.length > 0) {
        return directMatches as ViscosityResult[];
      }

      const nested = payload.results.flatMap((item: any) => extractViscosityResults(item?.result));
      if (nested.length > 0) {
        return nested;
      }

      if (payload.results.length > 0 && typeof payload.results[0] === 'object') {
        return payload.results as ViscosityResult[];
      }
    }

    if (Array.isArray(payload)) {
      const directList = payload.filter((item: any) => item && typeof item === 'object' && 'name' in item && 'viscosity_class' in item);
      if (directList.length > 0) {
        return directList as ViscosityResult[];
      }
    }

    return [];
  };

  // 任务列表选择回调：与其他页面风格一致，点击“查看”打开结果
  const handleTaskSelected = (task: any) => {
    try {
      setTaskId(task.id);
      setTaskStatus(task);
      const normalizedStatus = (task.status || '').toLowerCase();

      // 合并历史任务的参数中的序列（如果有）
      const paramsAb = task?.parameters?.antibodies || [];
      if (Array.isArray(paramsAb) && paramsAb.length > 0) {
        setSubmittedAntibodies(paramsAb);
      }

      if (normalizedStatus !== 'completed') {
        message.info('任务仍在进行中，开始同步最新结果');
        pollTaskStatus(task.id);
        return;
      }

      if (task.result) {
        const extracted = extractViscosityResults(task.result);
        if (extracted.length > 0) {
          setResults(extracted);
          setShowResults(true);
        } else {
          message.warning('未能解析任务结果，尝试重新获取');
          pollTaskStatus(task.id);
        }
      } else if (task.result_url) {
        message.info('正在从结果链接获取任务详情');
        pollTaskStatus(task.id);
      }

      if (['failed', 'cancelled', 'timeout'].includes(normalizedStatus)) {
        message.warning(`任务状态：${task.status}`);
      }
    } catch (e) {
      console.error('处理任务选择失败: ', e);
    }
  };

  // 添加新的抗体输入组
  const addAntibody = () => {
    const newId = (antibodies.length + 1).toString();
    setAntibodies([...antibodies, { 
      id: newId, 
      name: '', 
      heavy_chain: '', 
      light_chain: '' 
    }]);
  };

  // 删除抗体输入组
  const removeAntibody = (id: string) => {
    if (antibodies.length > 1) {
      setAntibodies(antibodies.filter(ab => ab.id !== id));
    }
  };

  // 更新抗体数据
  const updateAntibody = (id: string, field: keyof AntibodyInput, value: string) => {
    setAntibodies(antibodies.map(ab => 
      ab.id === id ? { ...ab, [field]: value } : ab
    ));
  };

  // 生成默认名称
  const generateDefaultNames = (antibodyList: AntibodyInput[]) => {
    return antibodyList.map((ab, index) => ({
      ...ab,
      name: ab.name.trim() || `ab${index + 1}`
    }));
  };

  // 提交预测任务
  const handleSubmit = async () => {
    try {
      // 验证输入
      const validAntibodies = antibodies.filter(ab => 
        ab.heavy_chain.trim() && ab.light_chain.trim()
      );

      if (validAntibodies.length === 0) {
        message.error('请至少输入一个完整的抗体序列（重链和轻链）');
        return;
      }

      setLoading(true);
      
      // 生成默认名称
      const antibodiesWithNames = generateDefaultNames(validAntibodies);
      // 存储已提交的抗体（用于结果展示序列）
      setSubmittedAntibodies(antibodiesWithNames);
      
      // 构建请求数据
      const requestData = {
        antibodies: antibodiesWithNames.map(ab => ({
          name: ab.name,
          heavy_chain: ab.heavy_chain.trim(),
          light_chain: ab.light_chain.trim()
        }))
      };

      // 提交任务到抗体服务
      const taskData = {
        name: `抗体粘度预测 - ${antibodiesWithNames.length}个抗体`,
        description: `使用DeepViscosity预测${antibodiesWithNames.length}个抗体的粘度类别`,
        service_name: 'antibody_service',
        task_type: 'antibody_viscosity',
        handler_name: 'predict_viscosity_json',
        parameters: requestData,
        priority: 1,
        timeout: 1800,
        max_retries: 2
      };

      const taskResponse = await createTask(taskData);
      
      if (taskResponse.id) {
        setTaskId(taskResponse.id);
        message.success(`粘度预测任务已提交，任务ID: ${taskResponse.id}`);
        
        // 开始轮询任务状态
        pollTaskStatus(taskResponse.id);
      }

    } catch (error) {
      console.error('提交任务失败:', error);
      message.error('提交任务失败，请检查网络连接或联系管理员');
    } finally {
      setLoading(false);
    }
  };

  // 轮询任务状态
  const pollTaskStatus = async (taskId: string) => {
    const maxAttempts = 60; // 最多轮询60次
    const interval = 3000; // 每3秒轮询一次
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const apiBase = import.meta.env.VITE_TASK_MANAGER_URL?.replace(/\/$/, '') || '/task_manager';
        const response = await fetch(`${apiBase}/tasks/${taskId}/status`);
        
        if (response.ok) {
          const taskResult = await response.json();
          setTaskStatus(taskResult);
          
          const polledStatus = (taskResult.status || '').toLowerCase();

          if (polledStatus === 'completed') {
            if (taskResult.result) {
              const extracted = extractViscosityResults(taskResult.result);
              if (extracted.length > 0) {
                setResults(extracted);
                setShowResults(true);
                message.success(`粘度预测完成！共预测${extracted.length}个抗体`);
              } else {
                console.warn('粘度预测结果格式不正确:', taskResult.result);
                message.warning('粘度预测完成，但结果格式不正确');
              }
            }
            setLoading(false);
            break;
          } else if (polledStatus === 'failed') {
            message.error(`任务执行失败: ${taskResult.error || '未知错误'}`);
            setLoading(false);
            break;
          }
        }
        
        // 等待后继续轮询
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, interval));
        }
        
      } catch (error) {
        console.error('轮询任务状态失败:', error);
      }
    }
  };

  // 结果表格列定义
  const resultColumns = [
    {
      title: '抗体名称',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '粘度概率均值',
      dataIndex: 'prob_mean',
      key: 'prob_mean',
      width: 140,
      render: (value: number | undefined) => {
        const num = typeof value === 'number' ? value : Number.NaN;
        return Number.isFinite(num) ? num.toFixed(4) : '-';
      },
    },
    {
      title: '粘度概率标准差',
      dataIndex: 'prob_std',
      key: 'prob_std',
      width: 150,
      render: (value: number | undefined) => {
        const num = typeof value === 'number' ? value : Number.NaN;
        return Number.isFinite(num) ? num.toFixed(4) : '-';
      },
    },
    {
      title: '粘度类别',
      dataIndex: 'viscosity_class',
      key: 'viscosity_class',
      width: 100,
      render: (value: number | undefined) => {
        if (value === 0 || value === 1) {
          return (
            <Tag color={value === 1 ? 'red' : 'green'}>
              {value === 1 ? '高粘度' : '低粘度'}
            </Tag>
          );
        }
        return <Tag>未知</Tag>;
      },
    },
    {
      title: '详细标签',
      dataIndex: 'viscosity_label',
      key: 'viscosity_label',
      width: 150,
    },
  ];

  // 结果展示表格列定义（含序列，专用结果显示器）
  const resultViewerColumns = [
    {
      title: '抗体名称',
      dataIndex: 'name',
      key: 'name',
      width: 140,
      fixed: 'left' as const,
    },
    {
      title: '重链序列',
      dataIndex: 'heavy_chain',
      key: 'heavy_chain',
      width: 280,
      render: (seq: string) => (
        <Tooltip title={<div style={{ maxWidth: 600, wordBreak: 'break-all' }}>{seq}</div>}>
          <Typography.Text copyable ellipsis={{ tooltip: false }} style={{ maxWidth: 260, display: 'inline-block' }}>
            {seq}
          </Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: '轻链序列',
      dataIndex: 'light_chain',
      key: 'light_chain',
      width: 280,
      render: (seq: string) => (
        <Tooltip title={<div style={{ maxWidth: 600, wordBreak: 'break-all' }}>{seq}</div>}>
          <Typography.Text copyable ellipsis={{ tooltip: false }} style={{ maxWidth: 260, display: 'inline-block' }}>
            {seq}
          </Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: '概率均值',
      dataIndex: 'prob_mean',
      key: 'prob_mean',
      width: 120,
      render: (value: number | undefined) => {
        const num = typeof value === 'number' ? value : Number.NaN;
        return Number.isFinite(num) ? num.toFixed(4) : '-';
      },
    },
    {
      title: '概率标准差',
      dataIndex: 'prob_std',
      key: 'prob_std',
      width: 140,
      render: (value: number | undefined) => {
        const num = typeof value === 'number' ? value : Number.NaN;
        return Number.isFinite(num) ? num.toFixed(4) : '-';
      },
    },
    {
      title: '粘度类别',
      dataIndex: 'viscosity_class',
      key: 'viscosity_class',
      width: 120,
      render: (value: number | undefined) => {
        if (value === 0 || value === 1) {
          return (
            <Tag color={value === 1 ? 'red' : 'green'}>
              {value === 1 ? '高粘度' : '低粘度'}
            </Tag>
          );
        }
        return <Tag>未知</Tag>;
      },
    },
    {
      title: '标签',
      dataIndex: 'viscosity_label',
      key: 'viscosity_label',
      width: 140,
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/antibody')}
          style={{ marginBottom: 16 }}
        >
          返回抗体分析
        </Button>
        
        <Typography>
          <Title level={2}>抗体粘度预测</Title>
          <Paragraph>
            使用DeepViscosity深度学习模型预测单克隆抗体的粘度类别（低粘度 ≤20 cP 或高粘度 &gt;20 cP）
          </Paragraph>
        </Typography>
      </div>

      {/* 模型信息 */}
      <Alert
        message="模型信息"
        description={
          <div>
            <p><strong>DeepViscosity</strong> 是基于深度学习的抗体粘度预测模型，具有以下特点：</p>
            <ul>
              <li>使用DeepSP提取30个空间特性描述符</li>
              <li>102个ANN集成模型进行预测</li>
              <li>预测粘度类别：低粘度(≤20 cP)或高粘度(&gt;20 cP)</li>
              <li>支持批量预测多个抗体</li>
            </ul>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* 输入表单 */}
      <Card title="抗体序列输入" style={{ marginBottom: 24 }}>
        <Form form={form} layout="vertical">
          {antibodies.map((antibody, index) => (
            <Card 
              key={antibody.id}
              size="small"
              title={`抗体 ${index + 1}`}
              style={{ marginBottom: 16 }}
              extra={
                antibodies.length > 1 && (
                  <Button 
                    type="text" 
                    danger 
                    icon={<DeleteOutlined />}
                    onClick={() => removeAntibody(antibody.id)}
                  >
                    删除
                  </Button>
                )
              }
            >
              <Row gutter={16}>
                <Col span={4}>
                  <Form.Item label="抗体名称（可选）">
                    <Input
                      placeholder={`默认: ab${index + 1}`}
                      value={antibody.name}
                      onChange={(e) => updateAntibody(antibody.id, 'name', e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item 
                    label="重链可变区序列" 
                    required
                  >
                    <TextArea
                      placeholder="输入重链可变区氨基酸序列"
                      rows={3}
                      value={antibody.heavy_chain}
                      onChange={(e) => updateAntibody(antibody.id, 'heavy_chain', e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item 
                    label="轻链可变区序列" 
                    required
                  >
                    <TextArea
                      placeholder="输入轻链可变区氨基酸序列"
                      rows={3}
                      value={antibody.light_chain}
                      onChange={(e) => updateAntibody(antibody.id, 'light_chain', e.target.value)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}

          <Space style={{ width: '100%', justifyContent: 'center' }}>
            <Button 
              type="dashed" 
              icon={<PlusOutlined />} 
              onClick={addAntibody}
            >
              添加抗体
            </Button>
            <Button 
              type="primary" 
              icon={<PlayCircleOutlined />}
              loading={loading}
              onClick={handleSubmit}
            >
              开始预测
            </Button>
          </Space>
        </Form>
      </Card>

      {/* 任务列表（统一风格） */}
      {!showResults && (
        <div style={{ marginBottom: 24 }}>
          <TaskList 
            autoRefresh={true} 
            refreshInterval={5000} 
            onTaskSelected={handleTaskSelected}
            serviceName="antibody_service"
            taskType="antibody_viscosity"
            handlerName="predict_viscosity_json"
          />
        </div>
      )}


      {/* 预测结果 */}
      <Modal
        title={
          <Space>
            <BarChartOutlined />
            粘度预测结果
          </Space>
        }
        open={showResults}
        onCancel={() => setShowResults(false)}
        footer={[
          <Button key="close" onClick={() => setShowResults(false)}>
            关闭
          </Button>
        ]}
        width={1100}
      >
        {results.length > 0 && (
          <>
            <Alert
              message={`预测完成！共预测 ${results.length} 个抗体`}
              type="success"
              style={{ marginBottom: 16 }}
              showIcon
            />
            <Table
              columns={resultViewerColumns}
              dataSource={results.map((result, index) => {
                const seq = submittedAntibodies.find(ab => (ab.name.trim() || `ab${index + 1}`) === result.name);
                return {
                  ...result,
                  heavy_chain: seq?.heavy_chain || '-',
                  light_chain: seq?.light_chain || '-',
                  key: `${result.name}-${index}`,
                };
              })}
              pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: [5, 10, 20] }}
              size="middle"
              scroll={{ x: 1000, y: 420 }}
              bordered
            />
            <Divider />
            <div style={{ fontSize: '12px', color: '#666' }}>
              <p><strong>说明：</strong></p>
              <ul>
                <li>粘度概率均值：102个模型预测概率的平均值</li>
                <li>粘度概率标准差：预测概率的标准差，反映预测的不确定性</li>
                <li>低粘度：≤20 cP，适合高浓度制剂</li>
                <li>高粘度：&gt;20 cP，可能需要优化制剂条件</li>
              </ul>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
